import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AnalysisStatus,
  AnalysisTrigger,
  OcppSessionAnalysis,
  OcppSessionAnalysisApiService
} from '../../api/ocpp-session-analysis-api.service';
import { LaunchSessionAnalysisDialogComponent } from './launch-session-analysis-dialog.component';
import { SessionGroupTableComponent } from './session-group-table.component';
import { analysisDelta, DeltaView, formatDuration, formatPercent } from './session-analysis-format';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-ocpp-session-analysis-page',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    SessionGroupTableComponent
  ],
  templateUrl: './ocpp-session-analysis-page.component.html',
  styleUrls: ['./ocpp-session-analysis-page.component.scss']
})
export class OcppSessionAnalysisPageComponent implements OnInit {
  private readonly api = inject(OcppSessionAnalysisApiService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  analyses: OcppSessionAnalysis[] = [];
  job: OcppSessionAnalysis | null = null;
  comparison: OcppSessionAnalysis | null = null;
  comparisonId = '';
  selectedId: string | null = null;
  loading = false;
  loadError = '';

  formatPercent = formatPercent;
  formatDuration = formatDuration;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearPoll());
  }

  ngOnInit(): void {
    this.refreshList(true);
  }

  get chronological(): OcppSessionAnalysis[] {
    return [...this.analyses].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  }

  get selectedIndex(): number {
    return this.chronological.findIndex(analysis => analysis.id === this.selectedId);
  }

  get comparisonOptions(): OcppSessionAnalysis[] {
    return this.analyses.filter(analysis => analysis.status === 'SUCCEEDED' && analysis.id !== this.selectedId);
  }

  selectAnalysis(id: string): void {
    if (!id) {
      return;
    }
    if (this.comparisonId === id) {
      this.comparisonId = '';
      this.comparison = null;
    }
    this.selectedId = id;
    this.loadJob(id);
  }

  previous(): void {
    const index = this.selectedIndex;
    if (index > 0) {
      this.selectAnalysis(this.chronological[index - 1].id);
    }
  }

  next(): void {
    const index = this.selectedIndex;
    if (index >= 0 && index < this.chronological.length - 1) {
      this.selectAnalysis(this.chronological[index + 1].id);
    }
  }

  onComparison(id: string): void {
    this.comparisonId = id;
    if (!id) {
      this.comparison = null;
      return;
    }
    this.api.get(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(job => {
      this.comparison = job;
    });
  }

  confirmDelete(): void {
    const current = this.job;
    if (!current) {
      return;
    }
    const when = new Date(current.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Supprimer l’analyse',
          content: `Supprimer l’analyse du ${when} ? Cette action est définitive.`,
          confirmLabel: 'Supprimer',
          cancelLabel: 'Annuler'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          this.deleteAnalysis(current.id);
        }
      });
  }

  launch(): void {
    this.dialog
      .open(LaunchSessionAnalysisDialogComponent)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(parameters => {
        if (!parameters) {
          return;
        }
        this.api.create(parameters).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: job => {
            this.snackBar.open('Analyse lancée', undefined, { duration: 2500 });
            this.selectedId = job.id;
            this.refreshList(false);
            this.loadJob(job.id);
          },
          error: (error: HttpErrorResponse) => {
            this.snackBar.open(this.errorText(error, 'Impossible de lancer l\'analyse'), 'Fermer', { duration: 5000 });
          }
        });
      });
  }

  delta(current: number, baseline: number | undefined, direction: 'higher' | 'lower' | 'neutral'): DeltaView | null {
    if (!this.comparison?.result) {
      return null;
    }
    return analysisDelta(current, baseline, direction);
  }

  triggerLabel(trigger: AnalysisTrigger): string {
    return trigger === 'SCHEDULED' ? 'Semaine' : 'Manuelle';
  }

  statusLabel(status: AnalysisStatus): string {
    switch (status) {
      case 'QUEUED':
        return 'En attente';
      case 'RUNNING':
        return 'En cours';
      case 'SUCCEEDED':
        return 'Terminée';
      case 'FAILED':
        return 'Échec';
    }
  }

  issueCount(severity: 'WARNING' | 'DANGER'): number {
    return this.job?.result?.issues.filter(issue => issue.severity === severity).length ?? 0;
  }

  scopeLabel(scope: string): string {
    switch (scope) {
      case 'CHARGE_POINT':
        return 'Borne';
      case 'NAMESPACE':
        return 'Organisation';
      case 'VENDOR':
        return 'Vendeur';
      case 'MODEL':
        return 'Modèle';
      case 'FIRMWARE':
        return 'Firmware';
      default:
        return scope;
    }
  }

  private deleteAnalysis(id: string): void {
    this.api.delete(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.comparisonId === id) {
          this.comparisonId = '';
          this.comparison = null;
        }
        const ordered = this.chronological;
        const index = ordered.findIndex(analysis => analysis.id === id);
        const fallback = ordered[index + 1]?.id ?? ordered[index - 1]?.id ?? null;
        this.analyses = this.analyses.filter(analysis => analysis.id !== id);
        if (this.selectedId === id) {
          this.clearPoll();
          this.job = null;
          this.selectedId = fallback;
          if (fallback) {
            this.loadJob(fallback);
          }
        }
        this.snackBar.open('Analyse supprimée', undefined, { duration: 2500 });
      },
      error: (error: HttpErrorResponse) => {
        this.snackBar.open(this.errorText(error, 'Impossible de supprimer l\'analyse'), 'Fermer', { duration: 5000 });
      }
    });
  }

  private refreshList(selectLatest: boolean): void {
    this.loading = true;
    this.api.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: analyses => {
        this.analyses = analyses;
        this.loading = false;
        this.loadError = '';
        if (selectLatest && !this.selectedId && analyses.length > 0) {
          this.selectAnalysis(analyses[0].id);
        }
      },
      error: () => {
        this.loading = false;
        this.loadError = 'Impossible de charger les analyses.';
      }
    });
  }

  private loadJob(id: string): void {
    this.clearPoll();
    this.api.get(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: job => {
        if (this.selectedId !== id) {
          return;
        }
        this.job = job;
        this.analyses = this.analyses.map(analysis => analysis.id === job.id ? { ...analysis, ...job, result: undefined } : analysis);
        if (job.status === 'QUEUED' || job.status === 'RUNNING') {
          this.pollTimer = setTimeout(() => this.loadJob(id), 3000);
        }
      },
      error: () => {
        this.loadError = 'Impossible de charger cette analyse.';
      }
    });
  }

  private clearPoll(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private errorText(error: HttpErrorResponse, fallback: string): string {
    const body = error.error as { detail?: string; message?: string } | null;
    return body?.detail || body?.message || fallback;
  }
}
